// Global
import React, { useRef, useState, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';

// Local
import { HeadlineFields } from 'lib/types/headlineFields';
import IframeResizer, { IFrameComponent } from '@iframe-resizer/react';
import { Preamble } from 'components/helpers/Molecules/Preamble/Preamble';
import { useIframeWidth } from 'lib/hooks/useIframeWidth';
import styles from './Iframe.module.scss';
export interface IframeProps extends HeadlineFields {
  iframeUrl: string | undefined;
  width: string | number | undefined;
  height: string | number | undefined;
  allowLocalNetworkAccess?: boolean;
}

type MessageData = {
  iframe: IFrameComponent;
  message?: string;
  height?: number;
  width?: number;
  type?: string;
} | null;

export const Iframe = (props: IframeProps) => {
  const licenseKey = process.env.NEXT_PUBLIC_IFRAME_RESIZER_KEY || '';
  const ref = useRef(null);
  const [messageData, setMessageData] = useState<MessageData | undefined>(undefined);
  const searchParams = useSearchParams();

  const onResized = (data: MessageData) => setMessageData(data);

  const onMessage = (data: MessageData) => {
    setMessageData(data);
  };

  // TODO: Handle parameter passing to iFrame based on Sitecore checkbox selection in future once it is implemented.
  // Build iframe URL with query params
  const iframeUrlWithParams = useMemo(() => {
    if (!props.iframeUrl) return undefined;

    try {
      const url = new URL(props.iframeUrl);

      searchParams?.forEach((value, key) => {
        url.searchParams.set(key, value);
      });

      return url.toString();
    } catch (error) {
      console.error('Invalid iframe URL:', error);
      return props.iframeUrl;
    }
  }, [props.iframeUrl, searchParams]);

  // Memoize width and height calculations to avoid unnecessary recalculations
  const { width, height } = useMemo(() => {
    return {
      width: messageData?.width || props.width,
      height: messageData?.height
        ? `${messageData.height}`
        : props.height
          ? `${props.height}`
          : undefined,
    };
  }, [messageData?.width, messageData?.height, props.width, props.height]);

  const { containerRef, calculatedWidth, calculatedHeight } = useIframeWidth({
    width,
    height,
  });

  const aspectRatio = width && height ? `${width}/${height}` : '16/9';

  return (
    <div
      data-component="authorable/shared/content/iframe/iframe"
      ref={containerRef}
      className={styles.iframeContainer}
    >
      <Preamble headline={props.headline} align="TopCenter" />
      {iframeUrlWithParams && (
        <IframeResizer
          license={licenseKey}
          log
          inPageLinks
          forwardRef={ref}
          onMessage={onMessage}
          onResized={onResized}
          src={iframeUrlWithParams}
          width={messageData?.width || !props.height ? '100%' : calculatedWidth}
          height={calculatedHeight}
          scrolling={true}
          style={{ border: 'none', aspectRatio: aspectRatio }}
          {...(props.allowLocalNetworkAccess && { allow: 'local-network-access' })}
        />
      )}
    </div>
  );
};
